import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ClockIcon, HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/solid";
import { statusTone } from "../../../components/QuestionList/questionStatus";
import { formatDateTime } from "../../../utils/formatDate";
import { cardClass, linkButtonClass } from "../../Exams/TestBuilder/ui";
import { QuestionStatusPill, STATUS_ICONS } from "./QuestionCard";
import { recordComments, userDisplayName } from "./reviewUtils";

// How many records show before "View full history".
const FIRST_RECORDS = 5;

const VOTE_TONES = {
  UPVOTED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  DOWNVOTED: "bg-red-50 text-red-700 ring-red-600/20",
};

function HistoryRecord({ record, authorUuid }) {
  const { t, i18n } = useTranslation();
  const isVote = record.type === "evaluation";
  const code = record.status?.code;
  const upvote = code === "UPVOTED";
  const Icon = isVote ? (upvote ? HandThumbUpIcon : HandThumbDownIcon) : STATUS_ICONS[code] || ClockIcon;
  const tone = isVote ? VOTE_TONES[code] || VOTE_TONES.DOWNVOTED : statusTone(code).badge;
  // A status change by the question's author is theirs: a resubmit, or a moderator archiving their own.
  const role = isVote
    ? t("question_detail.roles.checker")
    : authorUuid && record.user?.uuid === authorUuid
    ? t("question_detail.roles.author")
    : t("question_detail.roles.approver");
  const comments = recordComments(record);

  return (
    <li className="flex gap-3 py-4 last:pb-0">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ring-inset ${tone}`}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-medium text-slate-900">{userDisplayName(t, record.user)}</span>
            <span className="text-xs text-slate-500">({role})</span>
            {isVote ? (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${tone}`}>
                {upvote ? t("question_detail.history.upvoted") : t("question_detail.history.downvoted")}
              </span>
            ) : (
              record.status && <QuestionStatusPill status={record.status} size="sm" />
            )}
          </div>
          <time dateTime={record.created} className="whitespace-nowrap pt-0.5 text-xs tabular-nums text-slate-500">
            {formatDateTime(record.created, i18n.language)}
          </time>
        </div>
        {comments.map((comment, index) => (
          <p key={index} className="mt-1.5 whitespace-pre-line break-words text-sm leading-relaxed text-slate-700">
            {comment}
          </p>
        ))}
      </div>
    </li>
  );
}

/**
 * The question's history, newest first: status changes (who, to what, their comment) and the experts' votes.
 * `authorUuid` marks the author's own steps.
 */
export default function StatusHistory({ history, loading = false, failed = false, authorUuid = null }) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const records = showAll ? history : history.slice(0, FIRST_RECORDS);

  return (
    <section aria-labelledby="question-history-title" className={cardClass}>
      <div className="p-5 sm:p-6">
        <h3 id="question-history-title" className="mb-2 text-lg font-semibold tracking-tight text-slate-900">
          {t("question_detail.history.title")}
        </h3>

        {failed && (
          <p role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {t("question_detail.review.history_failed")}
          </p>
        )}

        {records.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {records.map((record) => (
              <HistoryRecord key={record.uuid} record={record} authorUuid={authorUuid} />
            ))}
          </ul>
        ) : (
          !failed && (
            <div className="flex flex-col items-center py-8 text-center">
              <span className="rounded-full bg-slate-100 p-3 text-slate-400">
                <ClockIcon className="h-6 w-6" aria-hidden="true" />
              </span>
              <p className="mt-3 text-sm text-slate-500">
                {loading ? t("question_detail.loading") : t("question_detail.history.empty")}
              </p>
            </div>
          )
        )}

        {/* A quiet link: reading on is not an action. */}
        {history.length > FIRST_RECORDS && (
          <div className="mt-3 flex justify-center border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              aria-expanded={showAll}
              className={`${linkButtonClass} max-md:min-h-[2.75rem]`}
            >
              {showAll ? t("question_detail.history.hide") : t("question_detail.history.view_full", { count: history.length })}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
